// 단위 관리 페이지.
// 상품 단위(100g/근/kg/팩/마리 등)를 관리자가 직접 등록/수정/삭제한다(마이그 0022).
//  - 코드는 식별자라 등록 시에만 정하고 이후 불변. 라벨/기준중량/무게가변/노출/순서는 수정 가능.
//  - 기준중량(base_grams)은 무게가변 단위(실중량 정산 대상)의 환산 기준이다. 비우면 고정 단위로 본다.
//  - 사용 중(해당 단위를 쓰는 상품 존재)인 단위는 삭제할 수 없고, '비활성'으로 숨길 수 있다.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UnitDto } from "@oo/shared";
import { Button, Card, useConfirm } from "@oo/ui";
import { PageHeader } from "@/shared/ui";
import {
  createUnit,
  deleteUnit,
  listUnitsAdmin,
  updateUnit,
} from "@/features/catalog/catalogApi.ts";
import { ApiError } from "@/api/client.ts";

// 단위 목록/등록 관리 화면.
export default function UnitsPage() {
  const qc = useQueryClient();
  const { data: units = [], isLoading } = useQuery({
    queryKey: ["admin", "units"],
    queryFn: listUnitsAdmin,
  });

  // 신규 등록 폼 상태.
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [weightVariable, setWeightVariable] = useState(false);
  const [baseGrams, setBaseGrams] = useState("");
  const [active, setActive] = useState(true);
  const [createError, setCreateError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () => {
      const grams = baseGrams.trim() ? Number.parseInt(baseGrams, 10) : null;
      return createUnit({
        code: code.trim(),
        label: label.trim(),
        baseGrams: grams && grams > 0 ? grams : null,
        weightVariable,
        isActive: active,
      });
    },
    onSuccess: () => {
      setCode("");
      setLabel("");
      setWeightVariable(false);
      setBaseGrams("");
      setActive(true);
      setCreateError(null);
      void qc.invalidateQueries({ queryKey: ["admin", "units"] });
    },
    onError: (err: unknown) => {
      setCreateError(err instanceof Error ? err.message : "등록 실패");
    },
  });

  function onCreate() {
    if (!code.trim()) {
      setCreateError("단위 코드를 입력하세요. (영문 소문자/숫자/밑줄)");
      return;
    }
    if (!label.trim()) {
      setCreateError("단위 이름을 입력하세요.");
      return;
    }
    // 무게가변 단위인데 기준중량이 없으면 실중량 환산이 불가하므로 안내.
    if (weightVariable && !baseGrams.trim()) {
      setCreateError("무게가변 단위는 기준중량(g)을 입력해야 합니다.");
      return;
    }
    createMut.mutate();
  }

  return (
    <div className="mx-auto flex max-w-screen-lg flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">
      <PageHeader
        title="단위"
        description="상품 가격 단위를 등록하고 관리합니다. 무게가변 단위는 기준중량(g)으로 실제 중량을 환산합니다."
      />

      <Card as="section">
        <h3 className="mb-3 text-sm font-semibold text-ink-900">신규 단위 등록</h3>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 md:flex-row">
            <label className="flex flex-1 flex-col gap-1">
              <span className="text-xs font-medium text-ink-600">코드 (식별자, 등록 후 변경 불가)</span>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={24}
                className="min-h-touch rounded border border-ink-300 bg-surface px-3 text-sm text-ink-900 placeholder:text-ink-500"
                placeholder="예: slice"
              />
            </label>
            <label className="flex flex-1 flex-col gap-1">
              <span className="text-xs font-medium text-ink-600">이름 (화면 표시)</span>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={20}
                className="min-h-touch rounded border border-ink-300 bg-surface px-3 text-sm text-ink-900 placeholder:text-ink-500"
                placeholder="예: 한 줄"
              />
            </label>
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={weightVariable}
                onChange={(e) => setWeightVariable(e.target.checked)}
              />
              무게가변 단위 (실중량 정산 대상)
            </label>
            <label className="flex flex-1 flex-col gap-1">
              <span className="text-xs font-medium text-ink-600">기준중량(g) (무게가변 시 필수)</span>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                value={baseGrams}
                onChange={(e) => setBaseGrams(e.target.value)}
                className="min-h-touch rounded border border-ink-300 bg-surface px-3 text-sm text-ink-900 placeholder:text-ink-500"
                placeholder="예: 100 (100g 단위) / 600 (근)"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              사용(노출)
            </label>
          </div>
          {createError ? <p className="text-xs text-danger-700">{createError}</p> : null}
          <Button type="button" onClick={onCreate} loading={createMut.isPending} className="self-start">
            {createMut.isPending ? "등록 중..." : "등록"}
          </Button>
        </div>
      </Card>

      <section className="overflow-hidden rounded-lg border border-ink-200 bg-surface shadow-card">
        <div className="border-b border-ink-200 p-4 text-sm font-semibold text-ink-900">
          전체 단위 ({units.length})
        </div>
        {isLoading ? (
          <p className="p-4 text-sm text-ink-600">불러오는 중...</p>
        ) : units.length === 0 ? (
          <p className="p-4 text-sm text-ink-600">등록된 단위가 없습니다.</p>
        ) : (
          <ul className="divide-y divide-ink-200">
            {units.map((unit, idx) => (
              <UnitRow
                key={unit.code}
                unit={unit}
                canMoveUp={idx > 0}
                canMoveDown={idx < units.length - 1}
                onMoveUp={() => moveUnit(qc, units, idx, idx - 1)}
                onMoveDown={() => moveUnit(qc, units, idx, idx + 1)}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// 순서 변경: 배열에서 위치를 옮긴 뒤 0..N-1 로 sort_order 를 다시 매겨 변경된 항목만 PATCH 한다.
//  - 단위 수가 적어 개별 PATCH 로도 충분하다(별도 일괄 reorder 엔드포인트 없이 단순화).
async function moveUnit(
  qc: ReturnType<typeof useQueryClient>,
  list: UnitDto[],
  fromIdx: number,
  toIdx: number,
) {
  const next = [...list];
  const [moved] = next.splice(fromIdx, 1);
  if (!moved) {
    return;
  }
  next.splice(toIdx, 0, moved);
  await Promise.all(
    next.map((u, i) => (u.sortOrder !== i ? updateUnit(u.code, { sortOrder: i }) : null)),
  );
  await qc.invalidateQueries({ queryKey: ["admin", "units"] });
}

// 단위 단일 행. 인라인 편집(라벨/기준중량/무게가변/노출).
function UnitRow({
  unit,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
}: {
  unit: UnitDto;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(unit.label);
  const [weightVariable, setWeightVariable] = useState(unit.weightVariable);
  const [baseGrams, setBaseGrams] = useState(unit.baseGrams != null ? String(unit.baseGrams) : "");
  const [active, setActive] = useState(unit.isActive);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () => {
      const grams = baseGrams.trim() ? Number.parseInt(baseGrams, 10) : null;
      return updateUnit(unit.code, {
        label,
        weightVariable,
        baseGrams: grams && grams > 0 ? grams : null,
        isActive: active,
      });
    },
    onSuccess: async () => {
      setEditing(false);
      setError(null);
      await qc.invalidateQueries({ queryKey: ["admin", "units"] });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "저장 실패");
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteUnit(unit.code),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin", "units"] });
    },
    onError: (err: unknown) => {
      // 사용 중(409)이면 안내.
      if (err instanceof ApiError && err.code === "CONFLICT") {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : "삭제 실패");
      }
    },
  });

  function onSave() {
    if (!label.trim()) {
      setError("단위 이름을 입력하세요.");
      return;
    }
    if (weightVariable && !baseGrams.trim()) {
      setError("무게가변 단위는 기준중량(g)을 입력해야 합니다.");
      return;
    }
    saveMut.mutate();
  }

  return (
    <li className="flex flex-col gap-3 p-4 md:flex-row md:items-start">
      {!editing ? (
        <div className="flex-1">
          <div className="text-sm font-medium text-ink-900">
            {unit.label}
            <span className="ml-2 text-xs font-normal text-ink-500">({unit.code})</span>
          </div>
          <div className="text-xs text-ink-600">
            {unit.weightVariable
              ? `무게가변 · 기준 ${unit.baseGrams ?? "-"}g`
              : "고정 단위"}{" "}
            · {unit.isActive ? "사용" : "숨김"} · 순서 {unit.sortOrder}
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-ink-600">이름</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={20}
              className="min-h-touch rounded border border-ink-300 bg-surface px-3 text-sm text-ink-900"
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-ink-700">
            <input
              type="checkbox"
              checked={weightVariable}
              onChange={(e) => setWeightVariable(e.target.checked)}
            />
            무게가변 단위 (실중량 정산 대상)
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-ink-600">기준중량(g)</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              value={baseGrams}
              onChange={(e) => setBaseGrams(e.target.value)}
              className="min-h-touch rounded border border-ink-300 bg-surface px-3 text-sm text-ink-900"
              placeholder="무게가변 단위만 입력"
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-ink-700">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            사용(노출)
          </label>
          {error ? <p className="text-xs text-danger-700">{error}</p> : null}
        </div>
      )}
      <div className="ml-auto flex flex-wrap gap-2 text-xs">
        <RowButton onClick={onMoveUp} disabled={!canMoveUp}>
          위로
        </RowButton>
        <RowButton onClick={onMoveDown} disabled={!canMoveDown}>
          아래로
        </RowButton>
        {!editing ? (
          <>
            <RowButton onClick={() => setEditing(true)}>수정</RowButton>
            <button
              type="button"
              onClick={async () => {
                const ok = await confirm({
                  title: "단위 삭제",
                  description: `'${unit.label}' 단위를 삭제하시겠습니까?`,
                  confirmLabel: "삭제",
                });
                if (ok) {
                  deleteMut.mutate();
                }
              }}
              disabled={deleteMut.isPending}
              className="rounded border border-ink-300 px-2 py-1 text-danger-700 hover:bg-danger-50 disabled:opacity-50"
            >
              삭제
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onSave}
              disabled={saveMut.isPending}
              className="rounded bg-brand-600 px-3 py-1 font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              저장
            </button>
            <RowButton
              onClick={() => {
                setLabel(unit.label);
                setWeightVariable(unit.weightVariable);
                setBaseGrams(unit.baseGrams != null ? String(unit.baseGrams) : "");
                setActive(unit.isActive);
                setError(null);
                setEditing(false);
              }}
            >
              취소
            </RowButton>
          </>
        )}
        {!editing && deleteMut.isError && error ? (
          <p className="basis-full text-xs text-danger-700">{error}</p>
        ) : null}
      </div>
    </li>
  );
}

// 행 내 작은 보조 액션 버튼(토큰 테두리).
function RowButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded border border-ink-300 px-2 py-1 hover:bg-ink-100 disabled:opacity-40"
    >
      {children}
    </button>
  );
}
